import React from 'react';
import { Tabs, Card, List, Space, Tag, Typography, Collapse } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import type { KrybotAgent, KrybotAgentVersion } from '../../../types/krybot';
import { AgentRunMonitor } from './AgentRunMonitor';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

export interface AgentDetailTabsProps {
  agent: KrybotAgent;
}

/** Profile, Versions, and Runs tabs for agent detail (keeps AgentDetail under ESLint size limits). */
export const AgentDetailTabs: React.FC<AgentDetailTabsProps> = ({ agent }) => (
  <>
    {agent.description && (
      <div className="agent-detail-section">
        <Paragraph>{agent.description}</Paragraph>
      </div>
    )}

    <Tabs defaultActiveKey="profile">
      <TabPane tab="Profile" key="profile">
        <div className="agent-detail-section">
          <h3>Persona Prompt</h3>
          <Card>
            <Paragraph>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {agent.persona_prompt || 'No persona prompt defined'}
              </pre>
            </Paragraph>
          </Card>
        </div>

        <div className="agent-detail-section">
          <h3>Capabilities</h3>
          <Card>
            <pre className="json-editor">{JSON.stringify(agent.capabilities || {}, null, 2)}</pre>
          </Card>
        </div>

        <div className="agent-detail-section">
          <h3>Default Context</h3>
          <Card>
            <pre className="json-editor">{JSON.stringify(agent.default_context || {}, null, 2)}</pre>
          </Card>
        </div>

        <div className="agent-detail-section">
          <h3>Input Schema</h3>
          <Card>
            <pre className="json-editor">{JSON.stringify(agent.input_schema || {}, null, 2)}</pre>
          </Card>
        </div>

        <div className="agent-detail-section">
          <h3>Output Schema</h3>
          <Card>
            <pre className="json-editor">{JSON.stringify(agent.output_schema || {}, null, 2)}</pre>
          </Card>
        </div>
      </TabPane>

      <TabPane tab="Versions" key="versions">
        <div className="agent-version-list">
          <List
            dataSource={agent.versions || []}
            renderItem={(version: KrybotAgentVersion) => (
              <List.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>{version.version_tag}</Text>
                    {version.is_active && (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        Active
                      </Tag>
                    )}
                    <Text type="secondary">
                      Created: {new Date(version.created_at).toLocaleString()}
                    </Text>
                  </Space>
                  {version.change_summary && <Paragraph>{version.change_summary}</Paragraph>}
                  {version.persona_prompt && (
                    <Collapse>
                      <Panel header="Persona Prompt" key="prompt">
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                          {version.persona_prompt}
                        </pre>
                      </Panel>
                    </Collapse>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </div>
      </TabPane>

      <TabPane tab="Runs" key="runs">
        <AgentRunMonitor agentId={agent.id} />
      </TabPane>
    </Tabs>
  </>
);
